import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
    loadStripe,
    Stripe,
    StripeElements,
    StripePaymentElement,
    PaymentIntentResult,
} from "@stripe/stripe-js";

import { DataService } from "../../core/providers/data/data.service";
import { StateService } from "../../core/providers/state/state.service";
import { NotificationService } from "../../core/providers/notification/notification.service";
import {
    PAYMENT_ERROR_MESSAGES,
    REDIRECT_STATUS,
} from "../../shared/constants/order.constants";
import { CreateStripePaymentIntentMutationResult } from "../checkout.types";
import {
    CHECKOUT_STRIPE_MESSAGES,
    STATE_SERVICE_KEY,
    STRIPE_JS,
    STRIPE_METHOD_CODE_SUBSTRING,
    stripeConfirmationReturnUrl,
    stripeConfirmationRouterPath,
} from "../components/checkout-payment/checkout-payment.constants";
import { CREATE_STRIPE_PAYMENT_INTENT } from "../components/checkout-payment/checkout-payment.graphql";

@Injectable({
    providedIn: "root",
})
export class CheckoutPaymentStripeService {
    stripe?: Stripe;
    clientSecret?: string;
    publishableKey?: string;
    elements?: StripeElements;
    private paymentElement?: StripePaymentElement;
    private destroy$ = new Subject<void>();

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private notificationService: NotificationService,
        private router: Router
    ) {}

    destroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroyPaymentElement();
        this.destroy$ = new Subject<void>();
    }

    destroyPaymentElement() {
        if (this.paymentElement) {
            try {
                this.paymentElement.destroy();
            } catch (error) {
                console.error("Error destroying payment element:", error);
            }
            this.paymentElement = undefined;
        }
        if (this.elements) {
            this.elements = undefined;
        }
    }

    async initializeStripe(
        publishableKey: string,
        onSuccess?: () => void,
        onError?: (msg: string) => void
    ) {
        this.publishableKey = publishableKey;
        if (!this.publishableKey) {
            onError?.(CHECKOUT_STRIPE_MESSAGES.PUBLISHABLE_KEY_UNAVAILABLE);
            return;
        }

        try {
            const loadedStripe = await loadStripe(this.publishableKey);
            if (!loadedStripe) {
                onError?.(CHECKOUT_STRIPE_MESSAGES.LOAD_FAILED);
                return;
            }
            this.stripe = loadedStripe;
        } catch (error) {
            console.error("Error loading Stripe:", error);
            onError?.(CHECKOUT_STRIPE_MESSAGES.INIT_FAILED);
            return;
        }

        this.stateService
            .select((state) => state.clientSecret)
            .pipe(takeUntil(this.destroy$))
            .subscribe((secret) => {
                if (secret) {
                    this.clientSecret = secret;
                    this.initStripeElements(secret);
                    onSuccess?.();
                } else {
                    this.createNewPaymentIntent(onSuccess);
                }
            });
    }

    initStripeElements(clientSecret: string) {
        if (!this.stripe) {
            return;
        }

        this.elements = this.stripe.elements({ clientSecret });
        this.clientSecret = clientSecret;
    }

    async mountPaymentElement(containerId: string, onMounted?: () => void) {
        const paymentElementContainer = document.getElementById(containerId);
        if (!paymentElementContainer || !this.elements) {
            return;
        }

        if (this.paymentElement) {
            this.paymentElement.destroy();
            this.paymentElement = undefined;
        }

        this.paymentElement = this.elements.create(STRIPE_JS.ELEMENT_TYPE_PAYMENT);
        this.paymentElement.mount(`#${containerId}`);
        onMounted?.();
    }

    async handleStripePayment(
        activeOrderCode: string | undefined
    ): Promise<PaymentIntentResult | undefined> {
        if (!this.stripe || !this.elements || !this.clientSecret) {
            this.notificationService
                .error(PAYMENT_ERROR_MESSAGES.STRIPE_NOT_INITIALIZED)
                .subscribe();
            return;
        }

        const returnUrl = stripeConfirmationReturnUrl(
            location.origin,
            activeOrderCode
        );

        const result = await this.stripe.confirmPayment({
            elements: this.elements,
            confirmParams: {
                return_url: returnUrl,
            },
            redirect: STRIPE_JS.REDIRECT_IF_REQUIRED,
        });

        if (result.paymentIntent?.status === REDIRECT_STATUS.SUCCEEDED) {
            this.router.navigate(
                [stripeConfirmationRouterPath(activeOrderCode ?? "")],
                {
                    queryParams: {
                        redirect_status: REDIRECT_STATUS.SUCCEEDED,
                    },
                }
            );
        } else {
            this.notificationService
                .error(CHECKOUT_STRIPE_MESSAGES.PAYMENT_FAILED_TRY_AGAIN)
                .subscribe();
        }

        if (result.error) {
            console.error("Stripe error:", result.error);
            this.notificationService
                .error(
                    result.error.message ??
                        CHECKOUT_STRIPE_MESSAGES.PAYMENT_ERROR
                )
                .subscribe();
        }

        return result;
    }

    createNewPaymentIntent(onSuccess?: () => void) {
        if (!this.stripe) {
            return;
        }

        this.dataService
            .mutate<CreateStripePaymentIntentMutationResult>(
                CREATE_STRIPE_PAYMENT_INTENT
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe((response) => {
                const newClientSecret = response.createStripePaymentIntent;
                if (newClientSecret) {
                    this.stateService.setState(
                        STATE_SERVICE_KEY.CLIENT_SECRET,
                        newClientSecret
                    );
                    this.clientSecret = newClientSecret;
                    this.initStripeElements(newClientSecret);
                    onSuccess?.();
                }
            });
    }

    isStripePaymentMethod(methodCode: string): boolean {
        return methodCode
            .toLowerCase()
            .includes(STRIPE_METHOD_CODE_SUBSTRING);
    }

    calculateStripeSurcharge(
        orderTotal: number,
        surchargePercent: number,
        selectedMethod?: string
    ): number {
        if (
            surchargePercent > 0 &&
            selectedMethod &&
            this.isStripePaymentMethod(selectedMethod)
        ) {
            return Math.round(orderTotal * (surchargePercent / 100));
        }
        return 0;
    }
}
